import { useCallback, useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  caminhoDaPlanilhaEmitida, caminhoDaPlanilhaPrevia, caminhoDaPrevia, caminhoDoPdfEmitido, codigoDaCampanha, enviarAnexo, enviarCroqui, montarRelatorio, verRelatorio,
  type AbacoDoPdf, type AnexoDoRelatorio, type CampanhaCompleta, type PaginasDoRelatorio, type RelatorioCompleto,
  type SecaoDoTexto,
} from '../../api/cptu';
import { numero as numeroBr, plural } from '../../formato';
import {
  AcaoBloqueada, Area, Aviso, BarraDeAvanco, Campo, Carregando, Dialogo, ErroDaApi, FotoProtegida, LinkProtegido,
  useEstreito, ZonaDeArquivo,
} from '../../ui';
import { AcaoSecundariaBloqueada } from './CampanhaMoldura';
import { CapturaDoCroqui, temWebgl, verticaisComCoordenada } from './CapturaDoCroqui';
import { descricaoDoGiro, fundoQueVale } from './locacao';
import { AcoesDoFluxo, FaixaDoFluxo, revisao, revisaoAberta, textoDaRevisao } from './FluxoDoRelatorio';
import { FormularioDaJanela } from './FormularioDaJanela';

/**
 * A aba Relatório da campanha CPTu — onda 315, Tarefa 315.5 (a prancheta aprovada «6 · Relatório»; spec 07 §7 e §9;
 * R1, R3, R5, R7 e R8).
 *
 * O cabeçalho da aba: «Relatório 2609350-CPTU-01 · R00» (ou «a numerar no envio»), a pílula «≈ N folhas» (o tamanho
 * do documento medido pela API, ou o da composição), «Baixar a planilha (xlsx)» e «Prévia do PDF» (secundários, pelo
 * `LinkProtegido`) e as ações do fluxo (`AcoesDoFluxo`: o primário é o da situação). Embaixo, três colunas que quebram
 * sozinhas, como na prancheta:
 *
 *  · à esquerda, as sete seções do texto com a origem — padrão, editado ou do cadastro;
 *  · no centro, o editor da seção aberta (`?secao=5` no endereço: o F5 a mantém): o texto com os campos entre chaves
 *    como fichas que se inserem onde está o cursor, o campo desconhecido acusado na linha (e o «Gravar» não chama a
 *    API), «Voltar ao texto padrão» e «Gravar»; a seção 4 só se lê — vem do cadastro de Equipamentos. Debaixo do
 *    editor, a faixa do fluxo (`FaixaDoFluxo`);
 *  · à direita, «O que entra no PDF»: Campanha (o quadro-resumo, o croqui — com a miniatura, «Capturar do mapa»,
 *    «Trocar por imagem» e «Voltar ao croqui desenhado» —, os perfis integrados e a análise integrada, que só existe
 *    com o gerador da onda 316), Em cada vertical (o boletim, as trilhas coloridas pela zona, os ábacos — o de
 *    Schneider bloqueado: sem gabarito, RN-252 —, os parâmetros homologados, as camadas, as dissipações e as fotos,
 *    com o aviso do HEIC; e, desde a onda 337, «Folhas das verticais em pé», o padrão) e Anexos (três caixas desde a
 *    onda 337 — premissas, zeros e edições; equações e referências; conformidade e as regras `cam.*`, desligada por
 *    padrão — e os documentos anexos — a planta do cliente — com a ordem e «Retirar»).
 *
 * Abaixo, as revisões: cada uma com a data da emissão (e da substituição) e o PDF e a planilha dela.
 *
 * Quem não pode montar (a permissão, ou a campanha fora do rascunho e da devolvida) vê tudo para ler e as ações
 * bloqueadas com o motivo que a API escreveu (P-UI-24) — nenhuma chamada de escrita sai daqui. No celular a aba é só
 * consulta: a régua (na casca), a situação, o PDF emitido e a prévia — «O relatório se monta no computador.».
 */

const NOME_DA_ORIGEM = { padrao: 'padrão', editado: 'editado', cadastro: 'do cadastro' } as const;

/** Até quantos caracteres uma seção vai (escolha da tela, sem fonte — a mesma da API). */
const LIMITE_DA_SECAO = 6000;

/** Os ábacos que o PDF desenha, na ordem da prancheta (o de Schneider não: sem gabarito, RN-252). */
const ABACOS: { chave: AbacoDoPdf; rotulo: string }[] = [
  { chave: 'sbtn', rotulo: 'SBTn 2010 (Qtn × Fr)' },
  { chave: 'sbtnMod', rotulo: 'SBTn 2016 (modificado)' },
  { chave: 'sbt', rotulo: 'SBT (qt/pa × Rf)' },
  { chave: 'qtBq', rotulo: 'Qt1 × Bq (sem fronteiras)' },
];

/** Os campos entre chaves de um texto que não são os da casa (na ordem, sem repetir). */
function camposDesconhecidos(texto: string, campos: string[]): string[] {
  const vistos: string[] = [];
  for (const m of texto.matchAll(/\{[^{}\n]*\}/g)) {
    if (!campos.includes(m[0]) && !vistos.includes(m[0])) vistos.push(m[0]);
  }
  return vistos;
}

/** O texto com os campos entre chaves como fichas (para ler). */
function comFichas(texto: string): ReactNode[] {
  return texto.split(/(\{[^{}\n]*\})/g).map((p, i) => (/^\{[^{}\n]*\}$/.test(p)
    ? <span key={i} className="cptu-ficha-campo">{p}</span> : p));
}

/** Os parágrafos de um texto (a linha em branco separa). */
const paragrafos = (t: string) => t.split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean);

export function CampanhaRelatorio({ c, aoMudar, aoRelatorio }: {
  c: CampanhaCompleta;
  aoMudar(): Promise<void>;
  /** A casca desenha a régua com o estado do relatório. */
  aoRelatorio(r: RelatorioCompleto | null): void;
}) {
  const id = c.campanha.id;
  const codigo = codigoDaCampanha(c.campanha.sequencia);
  const [r, setR] = useState<RelatorioCompleto | null>(null);
  const [erro, setErro] = useState<unknown>(null);
  const estreito = useEstreito();
  /** Os textos que a pessoa está escrevendo e ainda não gravou, por seção. */
  const [rascunhos, setRascunhos] = useState<Partial<Record<SecaoDoTexto, string>>>({});

  const carregar = useCallback(async () => {
    try {
      const x = await verRelatorio(id);
      setR(x); aoRelatorio(x); setErro(null);
    } catch (e) { setErro(e); }
  }, [id, aoRelatorio]);
  useEffect(() => { void carregar(); }, [carregar]);

  /** Depois de uma ação: a campanha (a casca, a régua) e o relatório. */
  const mudou = useCallback(async (novo?: RelatorioCompleto) => {
    if (novo) { setR(novo); aoRelatorio(novo); }
    await Promise.all([aoMudar(), novo ? Promise.resolve() : carregar()]);
  }, [aoMudar, carregar, aoRelatorio]);

  if (erro && !r) return <ErroDaApi erro={erro} />;
  if (!r) return <Carregando texto="Montando o relatório da campanha" />;

  const rel = r.relatorio;
  const aberta = revisaoAberta(r);
  const numero = rel ? `${rel.numero} · ${revisao(aberta ? rel.revisao + 1 : rel.revisao)}` : null;
  // Sem número, o nome que a API dá à prévia (`RelatorioService.previa`): «2611352-campanha-CPTU-01» (P-DOC-30).
  const queda = rel ? `${rel.numero}-${revisao(aberta ? rel.revisao + 1 : rel.revisao)}` : `${c.os.numero}-campanha-${codigo}`;
  const folhas = r.paginasDoDocumento ?? r.composicao.length;
  const emitidas = r.revisoes.filter((x) => x.emitido);
  const sujo = (Object.keys(rascunhos) as SecaoDoTexto[])
    .some((k) => rascunhos[k] !== r.secoes.find((s) => s.chave === k)?.texto);

  const cabeca = (
    <header className="cptu-relatorio-cabeca">
      <h2>
        Relatório{' '}
        {numero ? <span className="mono">{numero}</span> : <span className="cptu-relatorio-sem-numero">· a numerar no envio</span>}
      </h2>
      <span className="cptu-relatorio-folhas">≈ {plural(folhas, 'folha', 'folhas')}</span>
      <span className="cptu-relatorio-cabeca-vao" />
      {!estreito && (
        <LinkProtegido className="botao secundario" caminho={caminhoDaPlanilhaPrevia(id)} nomeQueda={`${queda}-PRELIMINAR.xlsx`}>
          Baixar a planilha (xlsx)
        </LinkProtegido>
      )}
      <LinkProtegido className="botao secundario" caminho={caminhoDaPrevia(id)} nomeQueda={`${queda}-PRELIMINAR.pdf`}>
        Prévia do PDF
      </LinkProtegido>
      {estreito && emitidas[0] && (
        <LinkProtegido className="botao secundario" caminho={caminhoDoPdfEmitido(emitidas[0].id)}
          nomeQueda={`${emitidas[0].numero}-${revisao(emitidas[0].revisao)}.pdf`}>
          PDF emitido ({revisao(emitidas[0].revisao)})
        </LinkProtegido>
      )}
      {!estreito && (
        <AcoesDoFluxo campanhaId={id} codigo={codigo} r={r} aoMudar={mudou} secundario={sujo} />
      )}
    </header>
  );

  const avisos = (
    <>
      {erro ? <ErroDaApi erro={erro} /> : null}
      {aberta && rel && (
        <Aviso tipo="info" titulo={`Revisão ${revisao(rel.revisao + 1)} em elaboração — a ${revisao(rel.revisao)} continua valendo até a ${revisao(rel.revisao + 1)} ser emitida.`}>
          {rel.motivoRevisao ? `Motivo: ${rel.motivoRevisao}.` : null}
        </Aviso>
      )}
      {!r.motor.homologada && r.situacao !== 'emitida' && (
        <p className="cptu-relatorio-nota">
          O motor CPTu <span className="mono">{r.motor.versao}</span> ainda não está assinado pelo RT: a prévia sai PRELIMINAR.
        </p>
      )}
    </>
  );

  if (estreito) {
    return (
      <section className="cptu-relatorio cptu-relatorio-consulta" aria-label="O relatório da campanha">
        {cabeca}
        {avisos}
        <Aviso tipo="info">O relatório se monta no computador.</Aviso>
        <FaixaDoFluxo r={r} />
        <Revisoes r={r} />
      </section>
    );
  }

  return (
    <section className="cptu-relatorio" aria-label="O relatório da campanha">
      {cabeca}
      {avisos}
      <div className="cptu-relatorio-corpo">
        <EditorDoTexto c={c} r={r} rascunhos={rascunhos} aoRascunho={setRascunhos} aoGravado={carregar} />
        <OQueEntraNoPdf c={c} r={r} aoMudarR={setR} aoMudar={mudou} />
      </div>
      <Revisoes r={r} />
    </section>
  );
}

/* ── O texto ────────────────────────────────────────────────────────── */

function EditorDoTexto({ c, r, rascunhos, aoRascunho, aoGravado }: {
  c: CampanhaCompleta; r: RelatorioCompleto;
  rascunhos: Partial<Record<SecaoDoTexto, string>>;
  aoRascunho(f: (antes: Partial<Record<SecaoDoTexto, string>>) => Partial<Record<SecaoDoTexto, string>>): void;
  aoGravado(): Promise<void>;
}) {
  const [busca, setBusca] = useSearchParams();
  const pedida = busca.get('secao');
  const secao = r.secoes.find((s) => s.chave === pedida) ?? r.secoes[0];
  const caixa = useRef<HTMLDivElement>(null);
  const [erro, setErro] = useState<unknown>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [tentou, setTentou] = useState(false);
  const podeEditar = r.pode.editar && secao.origem !== 'cadastro';
  const texto = rascunhos[secao.chave] ?? secao.texto;
  const sujo = texto !== secao.texto;
  const desconhecidos = camposDesconhecidos(texto, r.campos);
  const problema = desconhecidos.length
    ? `Campo desconhecido: ${desconhecidos.join(', ')}. Os campos são ${r.campos.join(', ')}.`
    : !texto.trim() ? 'A seção não fica vazia: «Voltar ao texto padrão» devolve o texto da casa.'
      : texto.length > LIMITE_DA_SECAO ? `A seção vai até ${numeroBr(LIMITE_DA_SECAO, 0)} caracteres.` : null;

  const abrir = (k: SecaoDoTexto) => {
    const p = new URLSearchParams(busca);
    p.set('secao', k);
    setBusca(p, { replace: true });
    setErro(null); setOk(null); setTentou(false);
  };

  const escrever = (v: string) => {
    aoRascunho((antes) => ({ ...antes, [secao.chave]: v }));
    setOk(null);
  };

  /** A ficha entra onde está o cursor (ou no fim). */
  const inserir = (campo: string) => {
    const t = caixa.current?.querySelector('textarea');
    const ini = t ? t.selectionStart : texto.length;
    const fim = t ? t.selectionEnd : texto.length;
    escrever(texto.slice(0, ini) + campo + texto.slice(fim));
    window.setTimeout(() => {
      if (!t) return;
      t.focus();
      t.setSelectionRange(ini + campo.length, ini + campo.length);
    }, 0);
  };

  const gravar = async (valor: string | null) => {
    setTentou(true);
    if (ocupado) return;
    if (valor !== null && (problema || !sujo)) return;
    setOcupado(true); setErro(null); setOk(null);
    try {
      await montarRelatorio(c.campanha.id, { textos: { [secao.chave]: valor } });
      aoRascunho((antes) => { const n = { ...antes }; delete n[secao.chave]; return n; });
      await aoGravado();
      setOk(valor === null ? 'De volta ao texto padrão.' : 'Gravado.');
      setTentou(false);
    } catch (e) { setErro(e); } finally { setOcupado(false); }
  };

  return (
    <>
      <nav className="cptu-relatorio-secoes" aria-label="As seções do texto">
        <p className="cptu-relatorio-secoes-titulo"><strong>Texto</strong> · padrão Testesolo, editável</p>
        {r.secoes.map((s) => {
          const marcado = rascunhos[s.chave] !== undefined && rascunhos[s.chave] !== s.texto;
          return (
            <button key={s.chave} type="button" className={`cptu-relatorio-secao${s.chave === secao.chave ? ' sel' : ''}`}
              aria-current={s.chave === secao.chave ? 'true' : undefined} onClick={() => abrir(s.chave)}
              data-secao={s.chave}>
              <span>{s.chave} · {s.titulo}</span>
              <span className={`cptu-relatorio-origem ${s.origem}`}>
                {marcado ? 'não gravado' : NOME_DA_ORIGEM[s.origem]}
              </span>
            </button>
          );
        })}
      </nav>
      <div className="cptu-relatorio-centro">
        <section className="cptu-relatorio-editor" aria-label={`Seção ${secao.chave} · ${secao.titulo}`}>
          <div className="cptu-relatorio-editor-cabeca">
            <h3>{secao.chave} · {secao.titulo}</h3>
            <span className={`cptu-relatorio-origem ${secao.origem}`}>{NOME_DA_ORIGEM[secao.origem]}</span>
          </div>
          {secao.origem === 'cadastro' ? (
            <>
              <div className="cptu-relatorio-texto">
                {paragrafos(secao.resolvido).map((p, i) => <p key={i}>{p}</p>)}
              </div>
              <p className="cptu-relatorio-nota">
                Vem do cadastro de Equipamentos (o cone, a série, o certificado e a validade na data de cada vertical) e
                não se edita aqui.
              </p>
            </>
          ) : podeEditar ? (
            <>
              <div ref={caixa} className="cptu-relatorio-area">
                <Area rotulo={`Texto da seção ${secao.chave}`} rows={12} value={texto} maxLength={LIMITE_DA_SECAO + 500}
                  erro={problema && (tentou || desconhecidos.length) ? problema : undefined}
                  dica="Os campos entre chaves se trocam pelos dados da campanha no PDF."
                  onChange={(e) => escrever(e.target.value)} />
              </div>
              <div className="cptu-relatorio-fichas" aria-label="Inserir um campo">
                <span className="cptu-relatorio-fichas-rotulo">Inserir campo:</span>
                {r.campos.map((f) => (
                  <button key={f} type="button" className="cptu-ficha-campo" onClick={() => inserir(f)}>{f}</button>
                ))}
              </div>
              <div className="cptu-relatorio-editor-acoes">
                {secao.origem === 'editado' && (
                  <button type="button" className="botao secundario" aria-disabled={ocupado}
                    onClick={() => { void gravar(null); }}>
                    Voltar ao texto padrão
                  </button>
                )}
                <button type="button" className={sujo ? 'botao' : 'botao secundario'} aria-disabled={ocupado || !sujo || !!problema}
                  onClick={() => { void gravar(texto); }}>
                  {ocupado ? 'Gravando…' : 'Gravar'}
                </button>
                {!sujo && <span className="cptu-relatorio-nota">Nada mudou nesta seção.</span>}
                {ok && <span className="cptu-relatorio-ok" role="status">{ok}</span>}
              </div>
              <ErroDaApi erro={erro} />
              <details className="cptu-relatorio-resolvido">
                <summary>Como sai no PDF (o texto gravado)</summary>
                {paragrafos(secao.resolvido).map((p, i) => <p key={i}>{p}</p>)}
              </details>
            </>
          ) : (
            <>
              <div className="cptu-relatorio-texto">
                {paragrafos(secao.texto).map((p, i) => <p key={i}>{comFichas(p)}</p>)}
              </div>
              <div className="cptu-relatorio-editor-acoes">
                <AcaoBloqueada texto="Gravar" motivo={r.motivos.editar ?? 'O texto não muda agora.'} />
              </div>
            </>
          )}
        </section>
        <FaixaDoFluxo r={r} />
      </div>
    </>
  );
}

/* ── O que entra no PDF ─────────────────────────────────────────────── */

function OQueEntraNoPdf({ c, r, aoMudarR, aoMudar }: {
  c: CampanhaCompleta; r: RelatorioCompleto;
  aoMudarR(r: RelatorioCompleto): void;
  aoMudar(): Promise<void>;
}) {
  const idDoMotivo = useId();
  const [erro, setErro] = useState<unknown>(null);
  const [ocupado, setOcupado] = useState(false);
  const pode = r.pode.editar;
  const p = r.paginas;
  const desc = pode ? undefined : idDoMotivo;

  /** As páginas vão inteiras (o PATCH troca o objeto); a tela já mostra a escolha, e a API confirma. */
  const mudar = async (parcial: Partial<PaginasDoRelatorio>) => {
    if (!pode || ocupado) return;
    const novas = { ...p, ...parcial };
    aoMudarR({ ...r, paginas: novas });
    setOcupado(true); setErro(null);
    try {
      await montarRelatorio(c.campanha.id, { paginas: novas });
      await aoMudar();
    } catch (e) { setErro(e); aoMudarR(r); } finally { setOcupado(false); }
  };

  const caixa = (rotulo: ReactNode, marcado: boolean, aoTrocar: (v: boolean) => void, extra?: ReactNode, chave?: string) => (
    <div className="cptu-pdf-opcao" data-opcao={chave}>
      <label className="cptu-pdf-caixa">
        <input type="checkbox" checked={marcado} disabled={!pode} aria-describedby={desc}
          onChange={(e) => aoTrocar(e.target.checked)} />
        <span>{rotulo}</span>
      </label>
      {extra}
    </div>
  );
  /** A opção que não se escolhe: marcada ou não, bloqueada, com o porquê escrito ao lado (P-UI-24). */
  const fixa = (rotulo: ReactNode, marcado: boolean, porque: string, chave?: string) => (
    <div className="cptu-pdf-opcao fixa" data-opcao={chave}>
      <label className="cptu-pdf-caixa">
        <input type="checkbox" checked={marcado} disabled aria-disabled="true" readOnly />
        <span>{rotulo}</span>
      </label>
      <span className="cptu-pdf-porque">{porque}</span>
    </div>
  );

  const homologados = r.parametrosPossiveis.filter((x) => x.homologada);
  const foraDoPdf = r.parametrosPossiveis.filter((x) => !x.homologada);
  const foraPorParametro = (g: string): string | null => {
    const lista = Object.entries(r.fora ?? {})
      .flatMap(([v, cols]) => cols.filter((x) => x.coluna === g).map((x) => ({ v, motivo: x.motivo })));
    if (!lista.length) return null;
    const motivo = lista[0].motivo.replace(/^fora das condições homologadas:\s*/i, '');
    return `fora das condições homologadas: ${motivo} (${lista.map((x) => x.v).join(', ')})`;
  };
  const totalDeVerticais = r.fotos.verticais.length;
  const linhasGravadas = c.campanha.linhas_corte?.length ?? 0;

  return (
    <aside className="cptu-relatorio-pdf" aria-label="O que entra no PDF">
      <h3>O que entra no PDF</h3>
      {!pode && <p className="cptu-motivo-bloqueio" id={idDoMotivo}>{r.motivos.editar}</p>}
      <ErroDaApi erro={erro} />

      <h4 className="cptu-pdf-grupo">Campanha</h4>
      {caixa('Quadro-resumo das verticais', p.quadroResumo, (v) => { void mudar({ quadroResumo: v }); }, undefined, 'quadroResumo')}
      {caixa('Croqui de locação', p.croqui, (v) => { void mudar({ croqui: v }); },
        <Croqui c={c} r={r} aoMudar={aoMudar} />, 'croqui')}
      {caixa('Perfis integrados (SBTn por cota)', p.perfisIntegrados, (v) => { void mudar({ perfisIntegrados: v }); }, undefined, 'perfisIntegrados')}
      {r.analiseIntegradaDisponivel
        ? caixa('Análise integrada (seção A–B e vista 3D)', p.analiseIntegrada, (v) => { void mudar({ analiseIntegrada: v }); }, undefined, 'analiseIntegrada')
        : fixa('Análise integrada (seção A–B e vista 3D)', false, 'vem com a análise integrada (onda 316)', 'analiseIntegrada')}
      {/* Onda 316 (316.6): dentro da Análise integrada, cada folha liga e desliga, como os anexos da 337. */}
      {r.analiseIntegradaDisponivel && p.analiseIntegrada && (
        <div className="cptu-pdf-subgrupo" role="group" aria-label="Análise integrada">
          {caixa(<>Seção A–B, uma folha por linha de corte{' '}
            <span className="cptu-pdf-porque">· {linhasGravadas ? plural(linhasGravadas, 'linha gravada', 'linhas gravadas')
              : 'nenhuma linha gravada: grave na aba Análise integrada'}</span></>,
          p.analiseSecoes, (v) => { void mudar({ analiseSecoes: v }); }, undefined, 'analiseSecoes')}
          {caixa(<>Vista 3D{' '}
            <span className="cptu-pdf-porque">· {c.campanha.vista3d_arquivo_id ? 'vista guardada'
              : 'sem vista guardada: a folha não sai (guarde no 3D da aba Análise integrada)'}</span></>,
          p.analiseVista3d, (v) => { void mudar({ analiseVista3d: v }); }, undefined, 'analiseVista3d')}
        </div>
      )}

      <h4 className="cptu-pdf-grupo">Em cada vertical</h4>
      {/* Onda 337: em pé (padrão), enquadradas, quatro colunas de gráfico; desmarcado, as folhas deitadas. */}
      {caixa('Folhas das verticais em pé (A4 vertical, quatro colunas de gráfico)', p.verticaisEmPe,
        (v) => { void mudar({ verticaisEmPe: v }); }, undefined, 'verticaisEmPe')}
      {caixa(p.verticaisEmPe ? 'Boletim: qt, fs, u₂, Rf e SBTn (o Ic abre os parâmetros)' : 'Boletim: qt, fs, u₂, Rf, Ic e SBTn',
        p.boletim, (v) => { void mudar({ boletim: v }); }, undefined, 'boletim')}
      {caixa('Trilhas coloridas pela zona (qt pelo SBT, Qtn pelo SBTn)', p.trilhasColoridasPorZona,
        (v) => { void mudar({ trilhasColoridasPorZona: v }); }, undefined, 'trilhasColoridasPorZona')}
      <div className="cptu-pdf-subgrupo" role="group" aria-label="Ábacos">
        <span className="cptu-pdf-subtitulo">Ábacos</span>
        {ABACOS.map((a) => caixa(a.rotulo, p.abacos.includes(a.chave), (v) => {
          void mudar({ abacos: v ? ABACOS.map((x) => x.chave).filter((k) => k === a.chave || p.abacos.includes(k))
            : p.abacos.filter((k) => k !== a.chave) });
        }, undefined, `abaco-${a.chave}`))}
        {fixa('Schneider (Q × U₂)', false, 'sem gabarito (RN-252)', 'abaco-schneider')}
      </div>
      <div className="cptu-pdf-subgrupo" role="group" aria-label="Parâmetros">
        <span className="cptu-pdf-subtitulo">
          Parâmetros · {plural(p.parametros.length, 'escolhido', 'escolhidos')}, de cinco em cinco por folha
        </span>
        <p className="cptu-pdf-porque cptu-pdf-escolhidos">
          {p.parametros.map((g) => homologados.find((x) => x.grandeza === g)?.rotulo ?? g).join(' · ') || 'nenhum'}
        </p>
        <details className="cptu-pdf-escolher">
          <summary>Escolher os parâmetros</summary>
        <div className="cptu-pdf-parametros">
          {homologados.map((x) => {
            const marcado = p.parametros.includes(x.grandeza);
            const fora = marcado ? foraPorParametro(x.grandeza) : null;
            return (
              <div key={x.grandeza} className="cptu-pdf-parametro" data-parametro={x.grandeza}>
                <label className="cptu-pdf-caixa">
                  <input type="checkbox" checked={marcado} disabled={!pode} aria-describedby={desc}
                    onChange={(e) => {
                      void mudar({ parametros: e.target.checked ? [...p.parametros, x.grandeza]
                        : p.parametros.filter((g) => g !== x.grandeza) });
                    }} />
                  <span>{x.rotulo}</span>
                </label>
                {fora && <span className="cptu-pdf-porque">{fora}</span>}
              </div>
            );
          })}
        </div>
        </details>
        {foraDoPdf.length > 0 && (
          <p className="cptu-pdf-porque">
            Fora do PDF (RN-252): {foraDoPdf.map((x) => `${x.rotulo}${x.motivo ? ` — ${x.motivo}` : ''}`).join('; ')}.
          </p>
        )}
      </div>
      {caixa('Camadas', p.camadas, (v) => { void mudar({ camadas: v }); }, undefined, 'camadas')}
      {caixa('Dissipações (onde houver)', p.dissipacoes, (v) => { void mudar({ dissipacoes: v }); }, undefined, 'dissipacoes')}
      {caixa(
        <>Fotos do equipamento no ponto <span className="cptu-pdf-porque">· {r.fotos.comFoto} de {totalDeVerticais} com foto</span></>,
        p.fotos, (v) => { void mudar({ fotos: v }); },
        r.fotos.heic > 0 ? (
          <span className="cptu-pdf-aviso">
            ▲ {plural(r.fotos.heic, 'foto em HEIC não vai', 'fotos em HEIC não vão')} ao PDF (o arquivo está guardado):
            envie em JPEG.
          </span>
        ) : undefined, 'fotos')}

      <h4 className="cptu-pdf-grupo">Anexos</h4>
      {/* Onda 337: os três anexos do sistema são opcionais; sem um deles, os seguintes sobem de letra. */}
      {caixa('Premissas, zeros e edições das verticais', p.premissas, (v) => { void mudar({ premissas: v }); }, undefined, 'premissas')}
      {caixa('Equações e referências', p.equacoes, (v) => { void mudar({ equacoes: v }); }, undefined, 'equacoes')}
      {caixa('Conformidade do software (resumo) e as regras', p.conformidade, (v) => { void mudar({ conformidade: v }); }, undefined, 'conformidade')}
      <Anexos c={c} r={r} aoMudar={aoMudar} />
    </aside>
  );
}

/* ── O croqui ───────────────────────────────────────────────────────── */

function Croqui({ c, r, aoMudar }: { c: CampanhaCompleta; r: RelatorioCompleto; aoMudar(): Promise<void> }) {
  const [capturando, setCapturando] = useState(false);
  const [trocando, setTrocando] = useState(false);
  const [erro, setErro] = useState<unknown>(null);
  const [ocupado, setOcupado] = useState(false);
  const cr = r.croqui;
  const pontos = verticaisComCoordenada(c).length;
  const fundoDaPrancha = fundoQueVale(r.paginas.pranchaFundo, cr?.origem);
  const giroDaPrancha = r.paginas.pranchaGiro ?? 0;
  const idDoMotivoDaCaptura = useId();
  const motivoDaCaptura = !r.pode.editar ? (r.motivos.editar ?? 'O croqui não muda agora.')
    : pontos === 0 ? 'Nenhuma vertical tem coordenada: grave a latitude e a longitude no cabeçalho de uma vertical para capturar o mapa.'
      : !temWebgl() ? 'Este navegador não desenha o mapa (sem WebGL): envie uma imagem por «Trocar por imagem».' : null;

  const desenhado = async () => {
    if (ocupado) return;
    setOcupado(true); setErro(null);
    try { await montarRelatorio(c.campanha.id, { croqui: null }); await aoMudar(); } catch (e) { setErro(e); } finally { setOcupado(false); }
  };

  return (
    <div className="cptu-croqui">
      <div className="cptu-croqui-linha">
        {cr?.arquivoId ? (
          <FotoProtegida arquivoId={cr.arquivoId} alt="O croqui de locação" className="cptu-croqui-miniatura"
            carregando={<span className="cptu-croqui-miniatura vazia">…</span>} />
        ) : <span className="cptu-croqui-miniatura vazia" aria-hidden="true">✛</span>}
        <span className="cptu-pdf-porque" data-croqui={cr?.origem ?? 'desenhado'}>
          {cr?.origem === 'mapa' ? `capturado do mapa${cr.fundo ? ` (${cr.fundo === 'mapa' ? 'mapa de ruas' : cr.fundo === 'satelite' ? 'satélite' : 'satélite com rótulos'})` : ''}`
            : cr?.origem === 'imagem' ? 'imagem enviada — sem escala gráfica'
              : 'desenhado com os pontos, a grade SIRGAS 2000, o norte e a escala'}
        </span>
      </div>
      <span className="cptu-pdf-porque" data-prancha-fundo={fundoDaPrancha} data-prancha-giro={giroDaPrancha}>
        Prancha: fundo {fundoDaPrancha === 'satelite' ? (cr?.origem === 'mapa' ? 'satélite' : 'satélite (sem captura: sai branco)') : 'branco'}
        {' · '}{descricaoDoGiro(giroDaPrancha)}
      </span>
      {r.verticaisSemCoordenada.length > 0 && (
        <span className="cptu-pdf-aviso">
          ▲ Sem coordenada: {r.verticaisSemCoordenada.join(', ')} (fica fora do croqui).
        </span>
      )}
      {r.pode.editar ? (
        <div className="cptu-croqui-acoes">
          {motivoDaCaptura ? (
            <span className="acao-bloqueada cptu-croqui-bloqueado">
              <button type="button" className="botao secundario" aria-disabled="true" aria-describedby={idDoMotivoDaCaptura}>
                Capturar do mapa
              </button>
              <small id={idDoMotivoDaCaptura} className="det">{motivoDaCaptura}</small>
            </span>
          ) : (
            <button type="button" className="botao secundario" onClick={() => setCapturando(true)}>Capturar do mapa</button>
          )}
          <button type="button" className="como-link" onClick={() => setTrocando(true)}>Trocar por imagem</button>
          {cr && (
            <button type="button" className="como-link" aria-disabled={ocupado} onClick={() => { void desenhado(); }}>
              Voltar ao croqui desenhado
            </button>
          )}
        </div>
      ) : null}
      <ErroDaApi erro={erro} />
      {capturando && (
        <CapturaDoCroqui c={c} r={r} aoFechar={() => setCapturando(false)}
          aoPronto={async () => { setCapturando(false); await aoMudar(); }} />
      )}
      {trocando && (
        <JanelaDaImagem campanhaId={c.campanha.id} aoFechar={() => setTrocando(false)}
          aoPronto={async () => { setTrocando(false); await aoMudar(); }} />
      )}
    </div>
  );
}

/** A largura e a altura da imagem escolhida (em pixel), para a moldura do croqui enviado. */
async function medidas(f: File): Promise<{ largura: number; altura: number }> {
  if (typeof createImageBitmap === 'function') {
    try {
      const b = await createImageBitmap(f);
      const m = { largura: b.width, altura: b.height };
      b.close();
      return m;
    } catch { /* cai na imagem */ }
  }
  return new Promise((resolver, recusar) => {
    const url = URL.createObjectURL(f);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolver({ largura: img.naturalWidth, altura: img.naturalHeight }); };
    img.onerror = () => { URL.revokeObjectURL(url); recusar(new Error(`«${f.name}» não abriu como imagem: envie um PNG ou um JPEG.`)); };
    img.src = url;
  });
}

function JanelaDaImagem({ campanhaId, aoFechar, aoPronto }: { campanhaId: string; aoFechar(): void; aoPronto(): Promise<void> }) {
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [erro, setErro] = useState<unknown>(null);
  const [progresso, setProgresso] = useState<number | null>(null);
  const enviar = async () => {
    if (!arquivo || progresso !== null) return;
    setErro(null); setProgresso(0);
    try {
      const m = await medidas(arquivo);
      const fd = new FormData();
      fd.append('imagem', arquivo, arquivo.name);
      fd.append('meta', JSON.stringify({ origem: 'imagem', largura: m.largura, altura: m.altura }));
      await enviarCroqui(campanhaId, fd, (f) => setProgresso(f));
      await aoPronto();
    } catch (e) { setErro(e); } finally { setProgresso(null); }
  };
  return (
    <Dialogo aberto titulo="Trocar o croqui por uma imagem" rotulo="Croqui de locação" aoFechar={aoFechar}>
      <FormularioDaJanela aoEnviar={() => { void enviar(); }}>
        <ErroDaApi erro={erro} />
        <p className="sub">
          A imagem entra como veio, sem escala gráfica (o PDF escreve «imagem enviada — sem escala gráfica»). Para a
          escala, capture do mapa.
        </p>
        <ZonaDeArquivo rotulo="Imagem do croqui" aceita="image/png,image/jpeg" tipos="PNG ou JPEG, até 12 MB"
          arquivos={arquivo} aoEscolher={(l) => setArquivo(l[0] ?? null)} desabilitado={progresso !== null} />
        {progresso !== null && <BarraDeAvanco pct={progresso * 100} rotulo="Enviando a imagem" />}
        <div className="rodape-dialogo">
          <button type="button" className="botao secundario" onClick={aoFechar}>Cancelar</button>
          <button type="submit" className="botao" aria-disabled={!arquivo || progresso !== null}>
            {progresso !== null ? 'Enviando…' : 'Usar esta imagem'}
          </button>
        </div>
      </FormularioDaJanela>
    </Dialogo>
  );
}

/* ── Os documentos anexos (acréscimo do Humberto, 12/10/2026) ───────── */

/** O anexo do relatório: «Anexo D», «E»… (A a C são do próprio relatório). */
const letraDoAnexo = (i: number) => String.fromCharCode(68 + i);
const ORIGEM_DO_ANEXO = { cliente: 'fornecido pelo cliente', testesolo: 'da Testesolo' } as const;

function Anexos({ c, r, aoMudar }: { c: CampanhaCompleta; r: RelatorioCompleto; aoMudar(): Promise<void> }) {
  const [anexando, setAnexando] = useState(false);
  const [erro, setErro] = useState<unknown>(null);
  const [ocupado, setOcupado] = useState(false);
  const lista = r.anexos;
  const pode = r.pode.editar;

  const gravarLista = async (nova: AnexoDoRelatorio[]) => {
    if (!pode || ocupado) return;
    setOcupado(true); setErro(null);
    try {
      await montarRelatorio(c.campanha.id, {
        anexos: nova.map((a) => ({ arquivoId: a.arquivoId, titulo: a.titulo, origem: a.origem })),
      });
      await aoMudar();
    } catch (e) { setErro(e); } finally { setOcupado(false); }
  };
  const mover = (i: number, d: -1 | 1) => {
    const nova = [...lista];
    [nova[i], nova[i + d]] = [nova[i + d], nova[i]];
    void gravarLista(nova);
  };

  return (
    <div className="cptu-pdf-subgrupo cptu-anexos" role="group" aria-label="Documentos anexos">
      <span className="cptu-pdf-subtitulo">Documentos anexos · no fim, numerados com o relatório</span>
      {lista.length === 0 ? (
        <p className="cptu-pdf-porque">Nenhum documento anexo (a planta de locação do cliente, por exemplo).</p>
      ) : (
        <ol className="cptu-anexos-lista">
          {lista.map((a, i) => (
            <li key={a.arquivoId} className="cptu-anexo" data-anexo={a.arquivoId}>
              <span className="cptu-anexo-titulo">
                <strong>Anexo {letraDoAnexo(i)}</strong> — {a.titulo}{' '}
                <span className="cptu-pdf-porque">
                  ({ORIGEM_DO_ANEXO[a.origem]}{a.paginas !== null ? ` · ${plural(a.paginas, 'folha', 'folhas')}` : ''})
                </span>
              </span>
              {pode && (
                <span className="cptu-anexo-acoes">
                  <button type="button" className="como-link" aria-label={`Subir «${a.titulo}»`} aria-disabled={i === 0 || ocupado}
                    onClick={() => { if (i > 0) mover(i, -1); }}>↑</button>
                  <button type="button" className="como-link" aria-label={`Descer «${a.titulo}»`}
                    aria-disabled={i === lista.length - 1 || ocupado}
                    onClick={() => { if (i < lista.length - 1) mover(i, 1); }}>↓</button>
                  <button type="button" className="como-link" aria-disabled={ocupado}
                    onClick={() => { void gravarLista(lista.filter((x) => x.arquivoId !== a.arquivoId)); }}>
                    Retirar
                  </button>
                </span>
              )}
            </li>
          ))}
        </ol>
      )}
      <ErroDaApi erro={erro} />
      {pode ? (
        <button type="button" className="botao secundario" onClick={() => setAnexando(true)}>Anexar documento</button>
      ) : (
        <AcaoSecundariaBloqueada texto="Anexar documento" motivo={r.motivos.editar ?? 'Os anexos não mudam agora.'} />
      )}
      {anexando && (
        <JanelaDoAnexo campanhaId={c.campanha.id} aoFechar={() => setAnexando(false)}
          aoPronto={async () => { setAnexando(false); await aoMudar(); }} />
      )}
    </div>
  );
}

function JanelaDoAnexo({ campanhaId, aoFechar, aoPronto }: { campanhaId: string; aoFechar(): void; aoPronto(): Promise<void> }) {
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [titulo, setTitulo] = useState('');
  const [origem, setOrigem] = useState<'cliente' | 'testesolo'>('cliente');
  const [erro, setErro] = useState<unknown>(null);
  const [progresso, setProgresso] = useState<number | null>(null);
  const [tentou, setTentou] = useState(false);
  const nome = useId();
  const falta = !arquivo ? 'Escolha o PDF.' : titulo.trim().length < 3 ? 'Dê um título ao anexo (3 caracteres ou mais).' : null;
  const enviar = async () => {
    setTentou(true);
    if (falta || progresso !== null || !arquivo) return;
    setErro(null); setProgresso(0);
    try {
      const fd = new FormData();
      fd.append('pdf', arquivo, arquivo.name);
      fd.append('meta', JSON.stringify({ titulo: titulo.trim(), origem }));
      await enviarAnexo(campanhaId, fd, (f) => setProgresso(f));
      await aoPronto();
    } catch (e) { setErro(e); } finally { setProgresso(null); }
  };
  return (
    <Dialogo aberto titulo="Anexar documento" rotulo="O que entra no PDF · Anexos" aoFechar={aoFechar}>
      <FormularioDaJanela aoEnviar={() => { void enviar(); }}>
        <ErroDaApi erro={erro} />
        <p className="sub">
          O PDF entra no fim do relatório, depois dos anexos da Testesolo, com a identificação e a numeração das folhas
          carimbadas em cada página. O arquivo fica guardado como veio.
        </p>
        <Campo rotulo="Título" value={titulo} maxLength={120} placeholder="Planta de locação"
          onChange={(e) => setTitulo(e.target.value)} />
        <fieldset className="cptu-anexo-origem">
          <legend>Origem</legend>
          <label><input type="radio" name={nome} checked={origem === 'cliente'} onChange={() => setOrigem('cliente')} /> Fornecido pelo cliente</label>
          <label><input type="radio" name={nome} checked={origem === 'testesolo'} onChange={() => setOrigem('testesolo')} /> Da Testesolo</label>
        </fieldset>
        <ZonaDeArquivo rotulo="Documento" aceita="application/pdf,.pdf" tipos="PDF, até 20 MB"
          arquivos={arquivo} aoEscolher={(l) => {
            const f = l[0] ?? null;
            setArquivo(f);
            if (f && !titulo.trim()) setTitulo(f.name.replace(/\.pdf$/i, '').replace(/[_-]+/g, ' ').trim());
          }} desabilitado={progresso !== null} />
        {tentou && falta && <p className="cptu-falta" role="status">{falta}</p>}
        {progresso !== null && <BarraDeAvanco pct={progresso * 100} rotulo="Enviando o documento" />}
        <div className="rodape-dialogo">
          <button type="button" className="botao secundario" onClick={aoFechar}>Cancelar</button>
          <button type="submit" className="botao" aria-disabled={!!falta || progresso !== null}>
            {progresso !== null ? 'Anexando…' : 'Anexar'}
          </button>
        </div>
      </FormularioDaJanela>
    </Dialogo>
  );
}

/* ── As revisões ────────────────────────────────────────────────────── */

function Revisoes({ r }: { r: RelatorioCompleto }) {
  const emitidas = r.revisoes.filter((x) => x.emitido);
  return (
    <section className="cptu-relatorio-revisoes" aria-label="As revisões emitidas">
      <h3>Revisões</h3>
      {emitidas.length === 0 ? (
        <p className="cptu-relatorio-nota">
          {r.relatorio ? 'Nenhuma revisão emitida ainda.' : 'O número do relatório nasce no envio para a verificação; nenhuma revisão emitida ainda.'}
        </p>
      ) : (
        <ul className="cptu-revisoes-lista">
          {emitidas.map((x) => (
            <li key={x.id} className="cptu-revisao" data-revisao={x.revisao}>
              <span><span className="mono">{x.numero}</span> · {textoDaRevisao(x)}</span>
              <span className="cptu-revisao-acoes">
                <LinkProtegido caminho={caminhoDoPdfEmitido(x.id)} nomeQueda={`${x.numero}-${revisao(x.revisao)}.pdf`}>
                  PDF {revisao(x.revisao)}
                </LinkProtegido>
                <LinkProtegido caminho={caminhoDaPlanilhaEmitida(x.id)} nomeQueda={`${x.numero}-${revisao(x.revisao)}.xlsx`}>
                  Planilha {revisao(x.revisao)} (xlsx)
                </LinkProtegido>
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
